import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Images } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { PROJECTS, CATEGORIES } from "@/lib/projects";
import Lightbox from "@/components/Lightbox";

const Portfolio = () => {
  const { t, lang } = useLang();
  const [activeCat, setActiveCat] = useState("all");
  const [activeProject, setActiveProject] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const filtered = useMemo(() => {
    if (activeCat === "all") return PROJECTS;
    return PROJECTS.filter((p) => p.category === activeCat);
  }, [activeCat]);

  const openProject = (project) => {
    setActiveProject(project);
    setLightboxIndex(0);
  };

  const closeLightbox = () => setActiveProject(null);

  // Items for lightbox: images of the currently open project
  const lightboxItems = activeProject
    ? activeProject.images.map((src, idx) => ({
        id: `${activeProject.id}-${idx}`,
        src,
        alt: activeProject.title[lang],
        title: activeProject.title[lang],
        category:
          CATEGORIES.find((c) => c.id === activeProject.category)?.label[lang] ||
          "",
      }))
    : [];
  const activeItem = lightboxItems[lightboxIndex] || null;

  return (
    <div
      className="bg-black text-white pt-32 md:pt-40 pb-24 md:pb-32 min-h-screen"
      data-testid="portfolio-page"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <span className="w-10 h-[1px] bg-[#E11D2E]" />
          <span className="text-[11px] uppercase tracking-[0.28em] font-medium text-white/55">
            {t.portfolio.eyebrow}
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-end mb-16 md:mb-20">
          <div className="lg:col-span-7">
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-light leading-[0.95] tracking-tight">
              {t.portfolio.title}
            </h1>
          </div>
          <div className="lg:col-span-5">
            <p className="text-base md:text-lg text-white/60 leading-relaxed max-w-md">
              {t.portfolio.subtitle}
            </p>
          </div>
        </div>

        {/* Filter chips */}
        <div
          className="flex flex-wrap gap-2 md:gap-3 mb-12 md:mb-16"
          role="tablist"
          aria-label={t.portfolio.filterAria}
          data-testid="portfolio-filters"
        >
          {CATEGORIES.map((cat) => {
            const isActive = cat.id === activeCat;
            return (
              <button
                key={cat.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveCat(cat.id)}
                data-testid={`filter-${cat.id}`}
                className={`px-5 py-3 text-[11px] uppercase tracking-[0.22em] font-medium border transition-colors duration-300 ${
                  isActive
                    ? "bg-[#E11D2E] border-[#E11D2E] text-white"
                    : "bg-transparent border-white/15 text-white/75 hover:border-white/45 hover:text-white"
                }`}
              >
                {cat.label[lang]}
              </button>
            );
          })}
        </div>

        {/* Project folder grid */}
        {filtered.length === 0 ? (
          <p className="text-white/50 py-20 text-center">{t.portfolio.empty}</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {filtered.map((project, idx) => (
              <ProjectFolder
                key={project.id}
                project={project}
                onOpen={openProject}
                testid={`portfolio-folder-${idx + 1}`}
                t={t}
                lang={lang}
              />
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="mt-24 md:mt-32 flex flex-col items-start">
          <span className="w-12 h-[2px] bg-[#E11D2E] mb-8" />
          <Link
            to="/contact"
            className="fd-btn"
            data-testid="portfolio-cta-contact"
          >
            {t.portfolio.cta} <ArrowRight size={14} />
          </Link>
        </div>
      </div>

      <Lightbox
        open={!!activeProject}
        onClose={closeLightbox}
        item={activeItem}
        items={lightboxItems}
        onNavigate={(next) => {
          const idx = lightboxItems.findIndex((i) => i.id === next.id);
          if (idx >= 0) setLightboxIndex(idx);
        }}
      />
    </div>
  );
};

const ProjectFolder = ({ project, onOpen, testid, t, lang }) => {
  const count = project.images.length;
  const catLabel =
    CATEGORIES.find((c) => c.id === project.category)?.label[lang] || "";
  return (
    <article
      className="group relative overflow-hidden bg-[#0a0a0a] border border-white/10 hover:border-white/30 transition-colors duration-300 cursor-pointer"
      onClick={() => onOpen(project)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(project);
        }
      }}
      tabIndex={0}
      role="button"
      aria-label={`${project.title[lang]} — ${count} ${count === 1 ? t.portfolio.imageCountSingle : t.portfolio.imageCount}`}
      data-testid={testid}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-black">
        <img
          src={project.cover}
          alt={project.title[lang]}
          className="absolute inset-0 w-full h-full object-cover img-zoom"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/0 to-black/0" />

        {/* Image-count badge */}
        {count > 1 && (
          <div className="absolute top-4 right-4 flex items-center gap-1.5 px-2.5 py-1.5 bg-black/70 backdrop-blur-sm border border-white/15 text-white text-[10px] uppercase tracking-[0.2em]">
            <Images size={11} />
            <span className="tabular-nums">{count}</span>
          </div>
        )}
      </div>

      {/* Caption */}
      <div className="p-5 md:p-6 flex items-start justify-between gap-4 border-t border-white/10">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.28em] text-[#E11D2E] mb-2">
            {catLabel}
          </p>
          <h3 className="font-display text-xl md:text-2xl font-light tracking-tight text-white">
            {project.title[lang]}
          </h3>
          {project.summary && (
            <p className="mt-2 text-sm text-white/55 leading-relaxed line-clamp-2">
              {project.summary[lang]}
            </p>
          )}
        </div>
        <span className="shrink-0 w-9 h-9 border border-white/15 flex items-center justify-center text-white/70 group-hover:bg-[#E11D2E] group-hover:border-[#E11D2E] group-hover:text-white transition-colors">
          <ArrowRight size={14} />
        </span>
      </div>
    </article>
  );
};

export default Portfolio;
